import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useToast } from "../../hooks/use-toast";
import api from "../../lib/api";
import { opsStatus, orderStatusEnum } from "../../lib/constants";
import { useAuth } from "../../lib/authContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface OrderStatusDropdownProps {
  currentStatus: string;
  orderId: number;
}

const OrderStatusDropdown: React.FC<OrderStatusDropdownProps> = ({
  currentStatus,
  orderId,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    setSelectedStatus(currentStatus);
  }, [currentStatus]);

  const availableStatuses = useMemo(() => {
    // Only show status dropdown if invoice is already generated
    // Check if current status is InvoiceGenerated or any status that comes after it
    const invoiceGeneratedStatuses = [
      "InvoiceGenerated",
      "Dispatched",
      "Transit",
      "Delivered",
      "RTO",
      "Lost",
      "DTO"
    ];

    if (!invoiceGeneratedStatuses.includes(currentStatus)) {
      // If invoice is not generated yet, don't show any options
      return [currentStatus];
    }

    // If invoice is generated, show only the 3 allowed statuses
    return opsStatus;
  }, [currentStatus]);

  const handleSubmit = async () => {
    if (selectedStatus !== currentStatus) {
      try {
        api
          .updateOrderStatus(orderId, selectedStatus, user?.id || "")
          .then(response => {
            window.location.reload();
          });
        toast({
          title: "Order Status Updated",
          description: `${selectedStatus}`,
        });
      } catch (error) {
        toast({
          title: "Failed to update status",
          description: `${error}`,
          variant: "destructive",
        });
      }
    }
  };

  return (
    <div className="w-full max-w-2xl">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mt-2 shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 tracking-wide uppercase">Update Status</div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex-1">
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-full bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {availableStatuses.map(status => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={selectedStatus === currentStatus}
            className={`px-6 py-2 rounded-md text-white font-medium transition-colors h-10
              ${
                selectedStatus !== currentStatus
                  ? "bg-gray-900 hover:bg-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 shadow-sm"
                  : "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed"
              }`}
          >
            Update
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderStatusDropdown;
